import { queryOptions } from '@tanstack/react-query';
import {
  listAutomationRules,
  getAutomationRule,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  listRuleOverrides,
  putRuleOverride,
  deleteRuleOverride,
} from '@/api/generated/sdk.gen';
import type {
  AutomationRuleSummary,
  AutomationRuleResponse,
  CreateAutomationRuleRequest,
  UpdateAutomationRuleRequest,
  AutomationRuleOverride,
} from '@/api/generated/types.gen';

export type {
  AutomationRuleSummary,
  AutomationRuleResponse,
  CreateAutomationRuleRequest,
  UpdateAutomationRuleRequest,
  AutomationRuleOverride,
};

export type KnownTrigger =
  | 'booking.confirmed'
  | 'payment.received'
  | 'booking.cancelled'
  | 'check_in'
  | 'check_out'
  | 'thread.opened'
  | 'thread.guest_replied';

export const KNOWN_TRIGGERS: KnownTrigger[] = [
  'booking.confirmed',
  'payment.received',
  'booking.cancelled',
  'check_in',
  'check_out',
  'thread.opened',
  'thread.guest_replied',
];

export const TRIGGER_LABELS: Record<KnownTrigger, string> = {
  'booking.confirmed': 'On booking',
  'payment.received': 'On payment received',
  'booking.cancelled': 'On cancellation',
  check_in: 'Check-in',
  check_out: 'Check-out',
  'thread.opened': 'Thread opened',
  'thread.guest_replied': 'Guest replied',
};

export interface RulesFilter {
  trigger?: KnownTrigger;
  onlyActive?: boolean;
  limit?: number;
  offset?: number;
}

export function rulesQuery(filter: RulesFilter = {}) {
  return queryOptions({
    queryKey: ['rules', filter] as const,
    queryFn: async ({ signal }) => {
      const res = await listAutomationRules({
        throwOnError: true,
        signal,
        query: {
          trigger: filter.trigger,
          only_active: filter.onlyActive,
          limit: filter.limit ?? 200,
          offset: filter.offset ?? 0,
        },
      });
      return res.data;
    },
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && count < 1;
    },
    staleTime: 60_000,
  });
}

// ruleQuery fetches ONE rule with its full material fields (timing, conditions).
// The list summary intentionally lacks them — an editor MUST load the full rule
// before populating a form, or a save will silently wipe those fields.
export function ruleQuery(ruleId: string) {
  return queryOptions({
    queryKey: ['rules', 'detail', ruleId] as const,
    queryFn: async ({ signal }) => {
      const res = await getAutomationRule({
        throwOnError: true,
        signal,
        path: { id: ruleId },
      });
      return res.data as AutomationRuleResponse;
    },
    enabled: !!ruleId,
  });
}

// ---------- Trigger classification ----------

export const ANCHOR_TRIGGERS = ['check_in', 'check_out'] as const;
export const EVENT_TRIGGERS = [
  'booking.confirmed',
  'payment.received',
  'booking.cancelled',
  'thread.opened',
  'thread.guest_replied',
] as const;

// ---------- Timing helpers ----------

export type TimingUnit = 'minutes' | 'hours' | 'days';
export type TimingDirection = 'before' | 'after';

export interface TimingFormState {
  direction: TimingDirection;
  value: number;
  unit: TimingUnit;
  atLocalTime: string; // 'HH:MM' or ''
}

/** Convert form state → stored timing object.  For event triggers returns {} (no timing). */
export function composeTiming(
  trigger: KnownTrigger,
  form: TimingFormState,
): { offset_minutes?: number; at_local_time?: string } {
  if (EVENT_TRIGGERS.includes(trigger as (typeof EVENT_TRIGGERS)[number])) return {};
  const multiplier: Record<TimingUnit, number> = { minutes: 1, hours: 60, days: 1440 };
  const signed =
    form.direction === 'before'
      ? -(form.value * multiplier[form.unit])
      : form.value * multiplier[form.unit];
  const result: { offset_minutes?: number; at_local_time?: string } = { offset_minutes: signed };
  if (form.atLocalTime && /^\d{2}:\d{2}$/.test(form.atLocalTime)) {
    result.at_local_time = form.atLocalTime;
  }
  return result;
}

/** Convert stored timing → form state. Reads legacy offset_hours too, so an
 * old rule round-trips faithfully instead of showing 0. */
export function decomposeTiming(
  trigger: KnownTrigger,
  timing: { offset_minutes?: number; offset_hours?: number; at_local_time?: string },
): TimingFormState {
  const DEFAULT: TimingFormState = { direction: 'before', value: 0, unit: 'hours', atLocalTime: '' };
  if (EVENT_TRIGGERS.includes(trigger as (typeof EVENT_TRIGGERS)[number])) return DEFAULT;
  const mins = timing.offset_minutes ?? (timing.offset_hours != null ? timing.offset_hours * 60 : 0);
  let value = Math.abs(mins);
  let unit: TimingUnit = 'minutes';
  if (value !== 0 && value % 1440 === 0) {
    value = value / 1440;
    unit = 'days';
  } else if (value !== 0 && value % 60 === 0) {
    value = value / 60;
    unit = 'hours';
  }
  return {
    direction: mins >= 0 ? 'after' : 'before',
    value,
    unit,
    atLocalTime: timing.at_local_time ?? '',
  };
}

// ---------- Update patch helper ----------

/** The material fields of a rule as sent to create/update. */
export interface RuleMaterialBody {
  name: string;
  trigger: KnownTrigger;
  template_id: string;
  timing: Record<string, unknown>;
  conditions: Record<string, unknown>;
}

/** Structural equality for plain-JSON values (objects, arrays, primitives). */
function jsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => jsonEqual(v, b[i]));
  }
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    jsonEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

// Keys the form actually models. Everything else in timing/conditions must be
// carried over from the original untouched — the form cannot edit what it does
// not render, so dropping such keys on save would be silent data loss.
const MODELED_TIMING_KEYS = ['offset_minutes', 'offset_hours', 'at_local_time'];
const MODELED_CONDITION_KEYS = ['source', 'had_prior_conversation', 'min_nights'];

/** Copy of obj without the given keys. */
function omitKeys(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));
}

/**
 * Overlay the form-composed material body onto the original rule so that keys
 * the form does not model survive a save:
 * - conditions: unmodeled keys (e.g. min_total_amount_minor) are carried over;
 * - timing: for event triggers the form composes {} while the server may hold
 *   a real offset (booking.confirmed honours offset_minutes) — the original
 *   timing is kept wholesale; for anchor triggers unmodeled keys are carried
 *   and modeled keys (incl. superseded legacy offset_hours) are replaced.
 * Identity when there is no original (create).
 */
export function mergeRuleBody(
  composed: RuleMaterialBody,
  original: RuleMaterialBody | undefined,
): RuleMaterialBody {
  if (!original) return composed;

  const conditions: Record<string, unknown> = {
    ...omitKeys(original.conditions, MODELED_CONDITION_KEYS),
    ...composed.conditions,
  };

  let timing: Record<string, unknown>;
  if (composed.trigger !== original.trigger) {
    // Trigger changed → old timing is anchored to the wrong event; take the
    // form's composition as-is.
    timing = { ...composed.timing };
  } else if (EVENT_TRIGGERS.includes(composed.trigger as (typeof EVENT_TRIGGERS)[number])) {
    timing = { ...original.timing };
  } else {
    timing = {
      ...omitKeys(original.timing, MODELED_TIMING_KEYS),
      ...composed.timing,
    };
  }

  return { ...composed, timing, conditions };
}

/**
 * Diff the edited body against the original rule → PATCH payload containing
 * ONLY the changed fields. An unchanged rule yields an empty patch (callers may
 * skip the request). Keeping unchanged material fields out of the PATCH also
 * avoids a pointless server-side queue re-materialization on name-only edits.
 */
export function buildRulePatch(
  original: RuleMaterialBody,
  next: RuleMaterialBody,
): UpdateAutomationRuleRequest {
  const patch: UpdateAutomationRuleRequest = {};
  if (next.name !== original.name) patch.name = next.name;
  if (next.trigger !== original.trigger) patch.trigger = next.trigger;
  if (next.template_id !== original.template_id) patch.template_id = next.template_id;
  if (!jsonEqual(next.timing, original.timing)) {
    patch.timing = next.timing as UpdateAutomationRuleRequest['timing'];
  }
  if (!jsonEqual(next.conditions, original.conditions)) {
    patch.conditions = next.conditions as UpdateAutomationRuleRequest['conditions'];
  }
  return patch;
}

// ---------- Overrides query ----------

export function ruleOverridesQuery(ruleId: string) {
  return queryOptions({
    queryKey: ['rules', ruleId, 'overrides'] as const,
    queryFn: async ({ signal }) => {
      const res = await listRuleOverrides({ throwOnError: true, signal, path: { id: ruleId } });
      return res.data as AutomationRuleOverride[];
    },
    enabled: !!ruleId,
    staleTime: 30_000,
  });
}

export { createAutomationRule, updateAutomationRule, deleteAutomationRule, listRuleOverrides, putRuleOverride, deleteRuleOverride };
